import React, { useState, useEffect } from 'react';
import '../../styles/socioeconomicoStyle/InfoResumo.css';
import {
  UsersRound,
  LandPlot,
  TrendingUp,
  MapPin,
  CircleDollarSign,
  HandCoins,
  Info
} from 'lucide-react';
import { RegiaoItem, socioeconomicoService, MunicipioResponse } from '../../API/socioeconomicoService';

interface InfoCard {
  titulo: string;
  valor: string | number;
  icone: React.ReactElement;
  descricao: string;
}

interface FiltrosProps {
  filtros: {
    estado: string;
    municipio: RegiaoItem | null;
  } | null;
}

const InfoResumo: React.FC<FiltrosProps> = ({ filtros }) => {
  const [cards, setCards] = useState<InfoCard[]>([]);
  const [popup, setPopup] = useState<{ visible: boolean; label: string }>({
    visible: false,
    label: '',
  });

  useEffect(() => {
    const fetchDados = async () => {
      if (!filtros?.municipio) {
        setCards([]);
        return;
      }

      try {
        const dados: MunicipioResponse = await socioeconomicoService.getDadosMunicipio(filtros.municipio);

        const novosCards: InfoCard[] = [
          {
            titulo: 'População total',
            valor: dados.habitantes.toLocaleString() + ' Hab.',
            icone: <UsersRound />,
            descricao: 'Número total de habitantes do município.'
          },
          {
            titulo: 'Área total',
            valor: `${dados.area.toLocaleString()} km²`,
            icone: <LandPlot />,
            descricao: 'Tamanho total da área geográfica da cidade.'
          },
          {
            titulo: 'IDH',
            valor: dados.idh,
            icone: <TrendingUp />,
            descricao: 'Índice de Desenvolvimento Humano.'
          },
          {
            titulo: 'PIB (Milhões)',
            valor: (Number(dados.pib) / 1_000_000).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
              minimumFractionDigits: 2
            }) + " milhões",
            icone: <CircleDollarSign />,
            descricao: 'Produto Interno Bruto total.'
          },
          {
            titulo: 'PIB per capita',
            valor: Number(dados.pib_per_capita).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
              minimumFractionDigits: 2
            }),
            icone: <MapPin />,
            descricao: 'PIB dividido pela população.'
          },
          {
            titulo: 'Pop. economicamente ativa',
            valor: `${dados.pop_economicamente_ativa.toLocaleString()} (${((dados.pop_economicamente_ativa / dados.habitantes) * 100).toFixed(1)}%)`,
            icone: <HandCoins />,
            descricao: 'População economicamente ativa.'
          },
        ];

        setCards(novosCards);
      } catch (error) {
        console.error('Erro ao buscar dados do município:', error);
        setCards([]);
      }
    };

    fetchDados();
  }, [filtros]);

  const handleInfoClick = (descricao: string) => {
    setPopup({ visible: true, label: descricao });
  };

  const fecharPopup = () => setPopup({ visible: false, label: '' });

  if (!filtros?.municipio) {
    return <p className="info-msg">Selecione um município para ver os dados.</p>;
  }

  return (
    <div className="info-resumo-container">
      {cards.length > 0 ? (
        cards.map((item, index) => (
          <div key={index} className="info-box">
            <div
              className="info-tooltip-wrapper"
              onClick={() => handleInfoClick(item.descricao)}
            >
              <Info className="info-icon-top" />
            </div>
            <div className="info-text">
              <h4>{item.titulo}</h4>
              <p>{item.valor}</p>
            </div>
            <div className="info-icon">
              <div className="icon-circle">{item.icone}</div>
            </div>
          </div>
        ))
      ) : (
        <p className="info-msg">Nenhum dado disponível para esse município.</p>
      )}

      {popup.visible && (
        <div className="info-popup-overlay" onClick={fecharPopup}>
          <div className="info-popup" onClick={e => e.stopPropagation()}>
            <h4>Explicação</h4>
            <p>{popup.label}</p>
            <button onClick={fecharPopup}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoResumo;
